import {
  foodResourceLabel,
  habitatResourceLabel,
  type FoodResourceKind,
  type HabitatResourceKind,
  type WildlifeGroup,
} from '@/flora/FloraEcology';
import type { ResolvedFlora } from '@/localization/FloraResolver';
import { windowContains, type DayWindow } from '@/localization/Phenology';
import { MONTH_LABELS } from '@/localization/Ecology';
import { dayOfYearFromDate } from '@/environment/SeasonModel';
import type { IconName } from './Icons';

/**
 * Site lenses: one reading of the planting, drawn where the plants actually are.
 *
 * ## Why this exists
 *
 * The app can already say *what* a planting feeds and *when*. `localization/Ecology.ts` resolves each
 * record's resources against this site's own phenology, and `buildWildlifeCalendar` rolls them into
 * monthly bands and finds the famine months. What nothing answered is **where** — and "where" is the
 * question a designer is actually holding a mouse to change. A March nectar gap reported as a band on
 * a chart is a fact; the same gap shown as a dark corner of the garden is an instruction.
 *
 * So a lens is not a new model. It is a *projection* of the resolved ecology onto one question, and
 * every number in it comes from `ResolvedFlora.ecology`, which the resolver has already shifted onto
 * this site's calendar. Move the site and every lens moves with it, because none of this is a date.
 *
 * ## Pure on purpose
 *
 * DOM-free, like `PlantManifest.ts` beside it, and for the same reason: three surfaces draw a lens —
 * the bar, the 3D view and the plan map — and if each computed its own reading they could disagree
 * about a plant. They read from here instead, so they cannot.
 *
 * ## The rule that matters most
 *
 * A record that does not answer a lens reads as **unrecorded**, never as zero. The distinction is not
 * pedantry: 67 of the 85 records carry no `deerResistant` flag, and drawing them as "not deer
 * resistant" would invent a claim about every one of them. {@link LensReading.described} is what the
 * renderers switch on to draw grey hatch rather than a cold value — the same discipline
 * `FloraEcology.ts` keeps about absent `floralMorphology`.
 */

/* ---------------------------------- The lenses ---------------------------------- */

export type LensId =
  | 'bird-food'
  | 'pollinator-food'
  | 'caterpillar-nursery'
  | 'mammal-food'
  | 'insect-habitat'
  | 'bird-shelter'
  | 'wildlife-value'
  | 'deer-resistance';

export interface LensDefinition {
  id: LensId;
  /** Short enough for a chip in the bar. */
  label: string;
  /** One line on what this lens answers — its tooltip, and the plan map's subtitle. */
  question: string;
  icon: IconName;
  /** Food resources that count, and who has to be among the consumers for them to count. */
  food?: { kinds: readonly FoodResourceKind[]; groups: readonly WildlifeGroup[] };
  /** Habitat resources that count, and who has to be among the users. */
  habitat?: { kinds: readonly HabitatResourceKind[]; groups: readonly WildlifeGroup[] };
  /** Whether a larval-host relationship contributes. Only the nursery lens sets it. */
  larvalHosts?: boolean;
  /**
   * True where the reading has no season, so the Today/Peak toggle is meaningless and hides itself.
   * Overall value is a property of the record; deer browse a garden in every month there is.
   */
  timeless?: boolean;
}

/**
 * The shipping set.
 *
 * Each one is the spatial form of a question the ecology model was already built to answer, and the
 * groups are not decoration — they are what stops "bird food" counting nectar a bird cannot drink.
 */
export const FLORA_LENSES: readonly LensDefinition[] = [
  {
    id: 'bird-food',
    label: 'Bird food',
    question: 'Where can a bird find something to eat, on this date?',
    icon: 'berry',
    food: {
      kinds: ['berries', 'fleshy-fruit', 'seeds', 'nuts'],
      groups: ['songbirds', 'other-birds'],
    },
  },
  {
    id: 'pollinator-food',
    label: 'Pollinator food',
    question: 'Where is there nectar and pollen while pollinators are flying?',
    icon: 'flower',
    food: {
      kinds: ['nectar', 'pollen'],
      groups: ['native-bees', 'butterflies', 'moths', 'hummingbirds', 'other-pollinators'],
    },
  },
  {
    id: 'caterpillar-nursery',
    label: 'Caterpillar nursery',
    question: 'Where can butterflies and moths actually raise the next generation?',
    icon: 'larva',
    food: { kinds: ['host-foliage'], groups: ['butterflies', 'moths'] },
    larvalHosts: true,
  },
  {
    id: 'mammal-food',
    label: 'Mammal food',
    question: 'Where is the mast and browse that carries mammals through the year?',
    icon: 'paw',
    food: {
      kinds: ['nuts', 'browse', 'fleshy-fruit', 'seeds'],
      groups: ['small-mammals', 'large-mammals'],
    },
  },
  {
    id: 'insect-habitat',
    label: 'Insect habitat',
    question: 'Where does an insect get through the winter alive?',
    icon: 'stems',
    habitat: {
      kinds: ['pithy-stems', 'standing-stems', 'leaf-litter', 'overwintering'],
      groups: ['native-bees', 'butterflies', 'moths', 'beneficial-insects', 'other-pollinators'],
    },
  },
  {
    id: 'bird-shelter',
    label: 'Bird shelter',
    question: 'Where is it safe to nest, and to sit out of the wind?',
    icon: 'nest',
    habitat: {
      kinds: ['nesting', 'cover', 'winter-cover', 'branch-structure', 'nesting-material'],
      groups: ['songbirds', 'other-birds'],
    },
  },
  {
    id: 'wildlife-value',
    label: 'Wildlife value',
    question: 'Overall — where is this garden doing something for wildlife at all?',
    icon: 'fauna',
    timeless: true,
  },
  {
    id: 'deer-resistance',
    label: 'Deer resistance',
    question: 'Where will browsing deer eat the design?',
    icon: 'deer',
    timeless: true,
  },
];

export function lensById(id: LensId): LensDefinition | null {
  return FLORA_LENSES.find((lens) => lens.id === id) ?? null;
}

/* --------------------------------- The reading --------------------------------- */

export interface LensContribution {
  label: string;
  value: number;
}

export interface LensReading {
  /** 0–1 on the day asked for. Zero where the plant offers nothing *today*, which is not nothing. */
  value: number;
  /** The best this plant ever offers this lens, and the day that starts — Peak mode reads these. */
  peakValue: number;
  peakDay: number;
  /** What contributed on the day asked for, strongest first. Empty when the value is zero. */
  contributions: LensContribution[];
  /**
   * False where the record simply does not answer this lens. Renderers must draw this as unrecorded
   * rather than as a zero — see the note at the top of this file.
   */
  described: boolean;
}

const UNRECORDED: LensReading = {
  value: 0,
  peakValue: 0,
  peakDay: 1,
  contributions: [],
  described: false,
};

/**
 * How a recorded larval-host relationship with no species count is scored.
 *
 * Deliberately mid-range: the relationship is documented, so it is emphatically not zero, and it is
 * not quantified, so claiming a high value would be inventing the figure the record declined to give.
 * Only 16 of the catalog's 70 host relationships carry a count.
 */
const UNCOUNTED_HOST_VALUE = 0.5;

/**
 * Turn "hosts 285 species of moth" into a 0–1 value.
 *
 * Saturating, and scaled so the catalog's real range (39 to 534, median 191) spreads across most of
 * the scale rather than pinning every oak and cherry at the top. The point of the nursery lens is to
 * distinguish the plants that carry the food web from the ones that merely join it.
 */
function hostValue(speciesCount: number | undefined): number {
  if (speciesCount === undefined) return UNCOUNTED_HOST_VALUE;
  return 1 - Math.exp(-speciesCount / 180);
}

/** Whether any of a resource's animals is one this lens is asking about. */
function serves(animals: readonly WildlifeGroup[], groups: readonly WildlifeGroup[]): boolean {
  return animals.some((animal) => groups.includes(animal));
}

interface Candidate {
  label: string;
  value: number;
  /** Absent for a timeless contribution, which is always in season. */
  window?: DayWindow;
}

/**
 * Everything this plant offers this lens, before the date is applied.
 *
 * Split out from {@link readLens} because both the value *today* and the value *at peak* are readings
 * of the same list, and computing the list twice is how the two would drift apart.
 */
function candidatesFor(lens: LensDefinition, resolved: ResolvedFlora): Candidate[] {
  const candidates: Candidate[] = [];
  const { ecology } = resolved;

  if (lens.food) {
    for (const resource of ecology.food) {
      if (!lens.food.kinds.includes(resource.kind)) continue;
      if (!serves(resource.consumers, lens.food.groups)) continue;
      candidates.push({
        label: foodResourceLabel(resource.kind),
        value: resource.value,
        window: resource.window,
      });
    }
  }

  if (lens.habitat) {
    for (const resource of ecology.habitat) {
      if (!lens.habitat.kinds.includes(resource.kind)) continue;
      if (!serves(resource.users, lens.habitat.groups)) continue;
      candidates.push({
        label: habitatResourceLabel(resource.kind),
        value: resource.value,
        window: resource.window,
      });
    }
  }

  if (lens.larvalHosts === true) {
    for (const host of ecology.larvalHosts) {
      // Hung on the foliage window rather than counted year-round: a caterpillar eats leaves, and a
      // host plant in February is hosting nothing. The window is the localized one, so the nursery
      // opens later in Vermont than in Georgia without this function knowing either place exists.
      candidates.push({
        label:
          host.speciesCount !== undefined
            ? `Larval host · ${host.speciesCount} species`
            : 'Larval host',
        value: hostValue(host.speciesCount),
        window: resolved.phenology.foliageWindow,
      });
    }
  }

  return candidates;
}

export interface ReadLensOptions {
  /**
   * Read every plant at its own best day rather than at the date given.
   *
   * One code path rather than a second function: peak is simply the reading with the calendar
   * ignored, so it cannot drift from the dated reading the way a parallel implementation would.
   */
  peak?: boolean;
}

/**
 * Read one plant against one lens, on one day.
 *
 * The value is the **strongest** thing the plant offers, not the sum of everything it offers. Summing
 * would push past 1 and need saturating, and the saturated number would answer a question nobody
 * asked: what a designer wants off a heat map is "is this a good bird plant right now", and the
 * answer to that is its best fruit, not its fruit plus its seed plus its nuts.
 */
export function readLens(
  lens: LensDefinition,
  resolved: ResolvedFlora,
  dayOfYear: number,
  options: ReadLensOptions = {},
): LensReading {
  if (lens.id === 'wildlife-value') {
    if (!resolved.ecology.described) return UNRECORDED;
    const score = resolved.ecology.score;
    return {
      value: score,
      peakValue: score,
      peakDay: 1,
      contributions: score > 0 ? [{ label: 'Ecological value', value: score }] : [],
      described: true,
    };
  }

  if (lens.id === 'deer-resistance') {
    // Read off the record rather than off `ResolvedEcology`, which does not carry the flag — and
    // `undefined` is the whole point here: it means unasked, and it must not become `false`.
    const resistant = resolved.definition.ecology?.deerResistant;
    if (resistant === undefined) return UNRECORDED;
    const value = resistant ? 1 : 0;
    return {
      value,
      peakValue: value,
      peakDay: 1,
      contributions: resistant ? [{ label: 'Reliably passed over by deer', value: 1 }] : [],
      described: true,
    };
  }

  if (!resolved.ecology.described) return UNRECORDED;

  const candidates = candidatesFor(lens, resolved);
  if (candidates.length === 0) {
    // The record was assessed and offers this lens nothing. That is a real answer — an oak is
    // genuinely not a nectar plant — so it reads as a described zero, not as unrecorded.
    return { value: 0, peakValue: 0, peakDay: 1, contributions: [], described: true };
  }

  let value = 0;
  let peakValue = 0;
  let peakDay = 1;
  const contributions: LensContribution[] = [];

  for (const candidate of candidates) {
    // A resource's value is constant across its window, so the peak needs no scan of the year: it is
    // the best candidate, and the day it starts.
    if (candidate.value > peakValue) {
      peakValue = candidate.value;
      peakDay = candidate.window?.startDay ?? 1;
    }
    const inSeason =
      options.peak === true || !candidate.window || windowContains(candidate.window, dayOfYear);
    if (!inSeason) continue;
    value = Math.max(value, candidate.value);
    contributions.push({ label: candidate.label, value: candidate.value });
  }

  contributions.sort((a, b) => b.value - a.value);
  return { value, peakValue, peakDay, contributions, described: true };
}

/* -------------------------------- Site summary -------------------------------- */

export interface LensSummary {
  /** Plants offering something on the day read. */
  offering: number;
  /** Plants whose record does not answer this lens at all. */
  unrecorded: number;
  /** Plants counted in total, unrecorded included. */
  total: number;
  /** The strongest reading anywhere on the site, so a renderer can say whether anything is hot. */
  strongest: number;
  /**
   * The day this lens next comes back, when nothing offers anything today but something will. Null
   * when the site is already offering, or when it never does.
   */
  returnsDay: number | null;
}

/**
 * Roll a set of readings into the line the bar prints.
 *
 * `returnsDay` is the part worth having: a lens that reads empty is ambiguous between "you planted
 * nothing for this" and "come back in May", and those call for opposite actions. It is the same
 * distinction `findGaps` draws in prose, asked here about a date instead of about a year.
 */
export function summarizeLens(readings: readonly LensReading[]): LensSummary {
  let offering = 0;
  let unrecorded = 0;
  let strongest = 0;
  let returnsDay: number | null = null;
  let bestPeak = 0;

  for (const reading of readings) {
    if (!reading.described) {
      unrecorded += 1;
      continue;
    }
    if (reading.value > 0) offering += 1;
    strongest = Math.max(strongest, reading.value);
    if (reading.peakValue > bestPeak) {
      bestPeak = reading.peakValue;
      returnsDay = reading.peakDay;
    }
  }

  return {
    offering,
    unrecorded,
    total: readings.length,
    strongest,
    returnsDay: offering > 0 ? null : returnsDay,
  };
}

/* ------------------------------- The heat ramp -------------------------------- */

/**
 * The one place the lens colour is decided.
 *
 * It lives in this module — which is otherwise pure data — because three renderers draw the same
 * reading in three technologies: emissive material in WebGL, SVG fills on the plan map, and CSS on
 * the bar's own legend. A ramp defined in any one of them would be re-guessed by the other two, and
 * a plant would end up a different pink in the view than on the plan of the view.
 *
 * Neon pink against foliage on purpose: nothing in a garden is that colour, so the overlay can never
 * be mistaken for something the design actually contains.
 */
export const LENS_HEAT = {
  /** Value 1 — the strongest thing on the site. */
  hot: '#ff2d95',
  /** The low end of a real reading. Still unmistakably the overlay, just dim. */
  cool: '#5c1f43',
  /** Not a low value — no value recorded. Deliberately outside the pink ramp entirely. */
  unrecorded: '#7b8794',
} as const;

/** Mix two `#rrggbb` strings. Small enough to keep here rather than reach for a colour library. */
function mixHex(from: string, to: string, t: number): string {
  const clamped = Math.max(0, Math.min(1, t));
  let out = '#';
  for (let channel = 0; channel < 3; channel += 1) {
    const start = parseInt(from.slice(1 + channel * 2, 3 + channel * 2), 16);
    const end = parseInt(to.slice(1 + channel * 2, 3 + channel * 2), 16);
    out += Math.round(start + (end - start) * clamped)
      .toString(16)
      .padStart(2, '0');
  }
  return out;
}

/** The colour a 0–1 reading is drawn in. `described: false` must use {@link LENS_HEAT.unrecorded}. */
export function lensHeatColor(value: number): string {
  return mixHex(LENS_HEAT.cool, LENS_HEAT.hot, value);
}

/* ---------------------------------- Formatting ---------------------------------- */

/** First day of each month, so a day of year can be named without a `Date`. */
const MONTH_STARTS: readonly number[] = MONTH_LABELS.map((_, index) => dayOfYearFromDate(index + 1, 1));

/** `MAY` — the month a day of year falls in, in the same voice the gap messages use. */
export function monthLabelForDay(dayOfYear: number): string {
  let month = 0;
  for (let index = 0; index < MONTH_STARTS.length; index += 1) {
    if (dayOfYear >= (MONTH_STARTS[index] ?? 1)) month = index;
  }
  return MONTH_LABELS[month] ?? '';
}

/**
 * The line under the lens chips.
 *
 * Says what is true and, where nothing is, says when it will be — never just "0 plants", which reads
 * as a verdict on the design when it is usually a verdict on the date.
 */
export function describeLensSummary(
  lens: LensDefinition,
  summary: LensSummary,
  peakMode: boolean,
): string {
  const subject = lens.label.toLowerCase();
  if (summary.total === 0) return 'Nothing planted yet.';

  const unrecorded =
    summary.unrecorded > 0 ? `  ·  ${summary.unrecorded} unrecorded` : '';

  if (summary.offering === 0) {
    if (summary.returnsDay !== null && !peakMode && lens.timeless !== true) {
      return `Nothing offers ${subject} today  ·  returns in ${monthLabelForDay(summary.returnsDay)}${unrecorded}`;
    }
    return `Nothing here offers ${subject}${unrecorded}`;
  }

  const when = peakMode || lens.timeless === true ? '' : ' today';
  return `${summary.offering} of ${summary.total} plants offer ${subject}${when}${unrecorded}`;
}
